import React, { FormEvent, useEffect, useState } from 'react';
import { Col, Form, FormGroup, Input, Label, Spinner } from 'reactstrap';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Btn, H2, P, Image } from '../AbstractElements';
import { dynamicImage } from '../Utils';
import { postJson } from '../hook/request';

type CheckResponse = {
    status?: string;
    message?: string;
};

type ChangeResponse = {
    status?: string;
    message?: string;
};

export default function ResetPasswordPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const code = searchParams.get('code') || '';

    const [checking, setChecking] = useState(true);
    const [valid, setValid] = useState(false);

    const [password, setPassword] = useState('');
    const [password2, setPassword2] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // 1) Verifica codice al mount
    useEffect(() => {
        const checkCode = async () => {
            if (!code) {
                setChecking(false);
                setValid(false);
                toast.error('Codice di recupero non valido o mancante.');
                return;
            }

            try {
                setChecking(true);
                const res = await postJson<CheckResponse>('/recover/check', { code });
                const payload: CheckResponse = (res as any)?.data ?? (res as any);

                if (String(payload.status).toLowerCase() === 'ok') {
                    setValid(true);
                } else {
                    setValid(false);
                    toast.error(payload.message || 'Link di recupero non valido o scaduto.');
                }
            } catch (err: any) {
                setValid(false);
                const msg =
                    err?.response?.data?.message ||
                    err?.message ||
                    'Errore durante la verifica del codice.';
                toast.error(msg);
            } finally {
                setChecking(false);
            }
        };

        checkCode();
    }, [code]);

    // 2) Submit nuova password
    const handleChangePassword = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!password || !password2) {
            toast.warn('Inserisci la nuova password in entrambi i campi.');
            return;
        }

        if (password !== password2) {
            toast.warn('Le password non coincidono.');
            return;
        }

        if (password.length < 6) {
            toast.warn('La password deve contenere almeno 6 caratteri.');
            return;
        }

        try {
            setSubmitting(true);
            const res = await postJson<ChangeResponse>('/recover/change', {
                code,
                password,
            });
            const payload: ChangeResponse = (res as any)?.data ?? (res as any);

            if (String(payload.status).toLowerCase() === 'ok') {
                toast.success(
                    payload.message || 'Password aggiornata con successo. Ora puoi fare login.'
                );
                navigate('/login');
            } else {
                toast.error(payload.message || 'Errore durante il cambio password.');
            }
        } catch (err: any) {
            const msg =
                err?.response?.data?.message ||
                err?.message ||
                'Errore durante il cambio password.';
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    // 3) Contenuto centrale (come SignInForm: dentro .login-main)
    let content: React.ReactNode;

    if (checking) {
        content = (
            <div className="text-center py-5">
                <Spinner size="sm" className="me-2" />
                <span>Verifica del link di recupero…</span>
            </div>
        );
    } else if (!valid) {
        content = (
            <div className="text-center py-5">
                <H2 className="mb-3">Link non valido</H2>
                <P>
                    Il link per il recupero password non è valido o è scaduto.
                    Richiedi nuovamente il recupero dalla pagina di login.
                </P>
                <Btn
                    color="primary"
                    className="mt-3"
                    onClick={() => navigate('/login')}
                >
                    Torna al login
                </Btn>
            </div>
        );
    } else {
        content = (
            <Form className="theme-form" onSubmit={handleChangePassword}>
                <H2 className="text-center">Imposta una nuova password</H2>
                <P className="text-center">
                    Inserisci la nuova password e confermala.
                </P>

                <FormGroup>
                    <Col>
                        <Label>Nuova password</Label>
                    </Col>
                    <Input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Nuova password"
                        required
                    />
                </FormGroup>

                <FormGroup>
                    <Col>
                        <Label>Ripeti password</Label>
                    </Col>
                    <Input
                        type="password"
                        value={password2}
                        onChange={(e) => setPassword2(e.target.value)}
                        placeholder="Ripeti password"
                        required
                    />
                </FormGroup>

                <FormGroup className="mb-0 checkbox-checked">
                    <div className="text-end mt-3 d-flex gap-2 justify-content-end">
                        <Btn
                            type="button"
                            color="light"
                            style={{ color: 'black' }}
                            onClick={() => navigate('/login')}
                        >
                            Annulla
                        </Btn>
                        <Btn color="primary" type="submit" disabled={submitting}>
                            {submitting ? 'Salvataggio…' : 'Salva nuova password'}
                        </Btn>
                    </div>
                </FormGroup>
            </Form>
        );
    }


    return (
        <div>
            <div>
            </div>
            <div className='login-card login-dark'>
                <div className="login-main">
                    {content}
                </div>
            </div>

        </div>
    );
}
